import { ChallengeActivity, ChallengeDefaultTab, ChallengeLocationSummary, ChallengeReward, ChallengeType, CombineOperator, GetPublicOrganizationProductResponse, IntegrationException, PaymentTargetType, RewardCriteria, TagSearchType, TranslationPhrases } from '../types';
import { GoogleTagEvent, getAccountInformation, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import challengeData, { challengeHasEnded, challengeLocationsDisplayName, challengeNotLive, challengeNotStarted, challengeTypeName } from '../helpers/state/challenge-data';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, delay, injectGoogleTagIfConfigured, isDefined, isValidString, setLinkTag, setLocalStorage, setManifest, setMetaTag, show_confetti, undefinedIfEmpty } from '../helpers/utils';
import { fonts, palette } from '../style/variables';
import { getActivatedChallenge, updateSavedLocation } from '../helpers/api/challenge-api';

import { ChallengePointCard } from '../components/challenge-point-card';
import { CheckinChallengeComponent } from '../components/checkin-challenge-component';
import { PreCheckoutComponent } from '../components/pre-checkout-component';
import { SettingDrawer } from '../components/mobile/setting-drawer';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { addChallengeView } from '../helpers/api/topics-api';
import { detectMobileViewport } from '../helpers/device';
import { getAccount } from '../helpers/api/account-api';
import { getMainPage } from '../helpers/page-name';
import { getOrCreateActivatedChallengeReferralCode } from '../helpers/api/referral-api';
import { formatPrice, getOrganizationProductsPublic } from '../helpers/api/products-api';
import { getQueryStringParameters } from '../helpers/urls';
import { loadGoogleMapsApi } from '../helpers/api/google-api';
import { parseDescriptionText } from '../helpers/embeds';
import pointCache from '../helpers/state/point-cache';
import { shouldRevealSpecialOffer } from '../helpers/challenge-special-offers';
import { startStripeCheckoutSession } from '../helpers/api/stripe-api';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

type ChallengePageSection = 'about' | 'checkins' | 'activities' | 'rewards' | 'leaderboard' | 'photo_feed' | 'share' | 'account';

@customElement('challenge-page')
export class ChallengePage extends LitElement {
    amplitude_callsite: string = 'Challenge Page';

    @query('#settingDrawer')
    settingDrawerElement!: SettingDrawer;

    @query('#search')
    searchElement!: HTMLInputElement;

    @query('#challengeCheckIn')
    checkInComponent!: CheckinChallengeComponent;

    @query('#challengePointCard')
    challengePointCard!: ChallengePointCard;

    @query('#preCheckout')
    private readonly preCheckoutElement?: PreCheckoutComponent | null;

    @property()
    challenge_id: string ='';

    @property({ type: Boolean })
    inner_content_only = false;

    @state()
    current_section: ChallengePageSection = 'about';

    @state()
    geolat = 0;

    @state()
    geolng = 0;

    @state()
    maxLoad: number = 25;

    private readonly ITEMS_PER_PAGE = 25;

    @state()
    private isLoadingMore: boolean = false;

    @state()
    private hasMoreItems: boolean = true;

    @state()
    focusedAward?: ChallengeReward;

    @state()
    pointsToAward = 0;

    @state()
    focusedLocation?: ChallengeLocationSummary;

    @state()
    focusedClue?: ChallengeLocationSummary;

    @state()
    focusedActivity?: ChallengeActivity;

    @state()
    newAwards: ChallengeReward[] = [];

    @state()
    settingDrawer?: 'Reward Details' | 'Location Details' | 'Check In' | 'Filters' | 'Activity Submission' | 'New Award' | 'Clue Details';

    @state()
    visitedTab?: string;

    @state()
    activityTab = 'remaining';

    @state()
    private pendingQrCodeKey?: string;

    @state()
    shareModalVisible = false;

    @state()
    selectedOptionalAmount: number = 0;

    @state()
    challengeProducts: GetPublicOrganizationProductResponse[] = [];

    @state()
    shareUrl?: string;

    @state()
    showHomeScreenVideo = false;

    private get filteredLocations() {
        if (challengeData.allPlacesHaveBeenVisited()) {
            return challengeData.challengeLocations.filter(location => location.checked_in === true && location.challenge_location_info?.is_active === true);
        }
        return challengeData.filteredChallengeLocations;
    }

    geolocationLoopStarted = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindChallengeDataState = new StateController(this, challengeData);

    private _lastKnownTotal: string = '';
    private _totalCheckInterval: number | undefined;

    connectedCallback() {
        super.connectedCallback();
        challengeData.challenge_page = this;
        const queryString = getQueryStringParameters();
        const qrCodeKey = queryString.get('challenge_location_qr_code_key');
        if (isDefined(qrCodeKey)) {
            this.pendingQrCodeKey = qrCodeKey;
        }

        this._totalCheckInterval = setInterval(() => {
            if (isDefined(this.preCheckoutElement)) {
                const currentTotal = this.preCheckoutElement?.totalCostString;
                if (currentTotal !== this._lastKnownTotal) {
                    this._lastKnownTotal = currentTotal;
                    this.requestUpdate();
                }
            }
        }, 500);
    }

    disconnectedCallback() {
        if (challengeData.challenge_page === this) {
            challengeData.challenge_page = undefined;
        }
        if (isDefined(this._totalCheckInterval)) {
            clearInterval(this._totalCheckInterval);
        }
        super.disconnectedCallback();
    }

    // Resolves this visitor's activated-challenge id from any available source: the URL/local storage
    // (topicData), an already-resolved registration (challengeData), or — for a signed-in user with no
    // local id — an account lookup. Returns undefined only when they genuinely have no registration.
    private async resolveActivatedChallengeId(): Promise<string | undefined> {
        let id = topicData.activatedChallengeId
            ?? challengeData.activatedChallengeId
            ?? challengeData.activated_challenge?._id.$oid;

        if (!isDefined(id) && accountInfo.isAuthenticated && isDefined(challengeData.challenge)) {
            try {
                await challengeData.setActivatedChallengeForUser();
            } catch (e) {
                getMainPage().errorToastElement.showToastAndThrow('Error loading your registration. Please try again later.');
                console.error('Failed to resolve activated challenge for signed-in user', e);
            }
            id = challengeData.activatedChallengeId ?? challengeData.activated_challenge?._id.$oid;
        }
        return id;
    }

    private redirectToRegistration() {
        // Already on (or heading to) registration — never re-redirect, or we spin forever.
        if (window.location.hash.startsWith('#registration')) {
            return;
        }
        getMainPage().errorToastElement.showToast('Let’s get you signed in for this challenge…');
        // Carry the QR key through so the registration page knows they arrived from a check-in scan.
        const qrParam = isDefined(this.pendingQrCodeKey) ? `&challenge_location_qr_code_key=${this.pendingQrCodeKey}` : '';
        // Swap the hash only. main-page's onhashchange re-renders into the registration page — NO full
        // reload. A forced reload here raced the navigation and caused an infinite refresh loop.
        window.location.hash = `#registration?topic_id=${topicData.topicId ?? ''}&topic_key=${topicData.topicKey ?? ''}&challenge_id=${topicData.challengeId ?? ''}${qrParam}`;
    }

    async firstUpdated() {
        // Set default lang; translation-data will override if translations are active
        if (!document.documentElement.lang) {
            document.documentElement.lang = translationData.language ?? 'en';
        }

        // get challenge ID and activated challenge id from the query parameters
        const queryString = getQueryStringParameters();
        const topicId = queryString.get('topic_id') ?? '';
        const topicKey = queryString.get('topic_key') ?? '';

        if (!this.inner_content_only) {
            try {
                await loadGoogleMapsApi('geometry');
            } catch (e) {
                console.error('Error loading Google Maps API', e);
            }
        }

        document.documentElement.style.setProperty('--custom-font', topicData.topic?.custom_font ?? fonts.mainFont);
        this.style.setProperty('font-family', topicData.topic?.custom_font ?? fonts.mainFont);

        // A stale/expired stored session would poison the topic load below and bounce the visitor to
        // the jarring global login. getAccount clears the session on a 401, so validate it up front:
        // afterwards they load as their real account or as a clean guest (→ challenge registration).
        if (isDefined(getAccountInformation()) && !accountInfo.isAuthenticated) {
            await getAccount(this.amplitude_callsite);
        }

        if (!topicData.isReady || !challengeData.isReady) {
            console.warn('Topic data is not ready, initializing topic data...');
            await topicData.instantiateTopicData(
                this.amplitude_callsite,
                accountInfo.isAuthenticated,
                true,
                true,
                topicId,
                topicKey
            );

            if (!isDefined(topicData.topic)) {
                console.error('Topic data is not defined after initialization.');
                getMainPage().errorToastElement.showToastAndThrow('Could not load challenge data. Please try again later.');
                return;
            }

            const resolvedActivatedChallengeId = await this.resolveActivatedChallengeId();

            if (!isDefined(resolvedActivatedChallengeId)) {
                // No registration anywhere (URL, local storage, or this account) — most often a generic
                // location QR scan by someone who hasn't registered yet. Send them to sign in / register.
                this.redirectToRegistration();
                return;
            }

            topicData.activatedChallengeId = resolvedActivatedChallengeId;
            topicData.activatedChallengeKey = topicData.activatedChallengeKey ?? challengeData.activatedChallengeKey;

            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                false,
                true,
                challengeData.allCheckins
            );

            // Prefer the registration we already resolved by account; otherwise fetch it by id/key.
            const activated_challenge = challengeData.activated_challenge
                ?? (await getActivatedChallenge(this.amplitude_callsite, topicData.activatedChallengeId, topicData.activatedChallengeKey));
            this.challenge_id = activated_challenge.challenge_id.$oid ?? '';

            await translationData.instantiateTranslations(true, true, true);

        } else {
            const resolvedActivatedChallengeId = await this.resolveActivatedChallengeId();
            if (!isDefined(resolvedActivatedChallengeId)) {
                // Data was already loaded but this visitor has no registration (URL, local storage, or
                // account) — redirect to sign in / register instead of hanging on the loading gate.
                this.redirectToRegistration();
                return;
            }
            topicData.activatedChallengeId = resolvedActivatedChallengeId;
            topicData.activatedChallengeKey = topicData.activatedChallengeKey ?? challengeData.activatedChallengeKey;
            const activated_challenge = challengeData.activated_challenge;
            this.challenge_id = activated_challenge?.challenge_id.$oid ?? '';
        }

        if (!challengeData.isReady || this.challenge_id !== challengeData.challenge?._id?.$oid) {
            console.warn('Challenge data is not ready, initializing challenge data...');
            await challengeData.instantiateChallengeData(
                this.challenge_id,
                topicData.topicKey,
                topicData.activatedChallengeId,
                topicData.activatedChallengeKey,
            );
        } else if (challengeData.challengeLocations.length === 0 && (challengeData.challenge?.locations.length ?? 0) > 0) {
            // Challenge data was instantiated (by instantiateTopicData) before the point cache
            // loaded, and the point-cache rebind only runs for live challenges — so a pre-launch
            // (tester) view arrives here with an empty location list. Bind now that points exist.
            await challengeData.setChallengeLocations();
        }
        translationData.onLanguageChanged(() => {
            void this.translateLabels();
        });
        await this.translateLabels();

        this.current_section = this.resolveDefaultSection();
        window.dispatchEvent(new CustomEvent('page-content-ready'));

        if (isDefined(this.pendingQrCodeKey)) {
            await this.handleQrCodeLink(this.pendingQrCodeKey);

            // Clear the QR code from the URL without refreshing
            const url = new URL(window.location.href);
            url.searchParams.delete('challenge_location_qr_code_key');
            window.history.replaceState({}, '', url.toString());

            // Clear the stored key
            this.pendingQrCodeKey = undefined;
        }

        try {
            await this.getChallengeProducts();
        } catch (e) {
            console.error('Error getting challenge products', e);
        }

        // Track challenge page view (client analytics + server telemetry)
        try {
            await addChallengeView(this.amplitude_callsite, topicData.topicId, this.challenge_id);
        } catch (e) {
            console.error('Error tracking challenge view', e);
        }
        logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.CHALLENGE_PAGE_VIEW, {
            challenge_id: this.challenge_id
        });

        // Set the OpenGraph data dynamically.
        try {
            setMetaTag('meta[property="og:title"]', 'content', `${this.activated_challenge?.display_name ?? this.activated_challenge?.email?.split('@')[0] ?? ''}'s Page - ${this.challenge?.name ?? ''}`);
            setMetaTag('meta[property="og:image"]', 'content', this.challenge?.registration_settings?.cover_image_url ?? this.challenge?.registration_settings?.logo_url ?? '');
            setLinkTag('link[rel="canonical"]', 'href', window.location.href);
            setMetaTag('meta[property="og:description"]', 'content', this.challenge?.description ?? '');
            if (detectMobileViewport()) {
                setManifest(this.challenge?.name ?? 'Passport',
                    window.location.href,
                    topicData.topic?.share_settings?.phone_short_name,
                    topicData.topic?.share_settings?.phone_icon_large ?? this.challenge?.registration_settings?.cover_image_url ?? topicData.topic?.custom_social_media_image ?? topicData.topic?.header_logo ?? '',
                    topicData.topic?.share_settings?.phone_icon_small ?? this.challenge?.registration_settings?.cover_image_url ?? topicData.topic?.custom_social_media_image ?? topicData.topic?.header_logo ?? '',
                    topicData.backgroundColor ?? '#ffffff',
                    topicData.backgroundColor ?? '#ffffff'
                );
                setMetaTag('meta[name="apple-mobile-web-app-capable"]', 'content', 'yes');
                setMetaTag('meta[name="apple-mobile-web-app-status-bar-style"]', 'content', 'black');
                setMetaTag('meta[name="apple-mobile-web-app-title"]', 'content', topicData.topic?.share_settings?.phone_short_name ?? this.challenge?.name ?? 'Challenge Passport');
                setMetaTag('meta[name="apple-touch-icon"]', 'content', topicData.topic?.share_settings?.phone_icon_small ?? topicData.topic?.custom_social_media_image ?? topicData.topic?.header_logo ?? '');
                setMetaTag('meta[name="viewport"]', 'content', 'initial-scale=1.0, viewport-fit=cover');
            };
        } catch (e) {
            console.error('Error setting all OpenGraph data', e);
        }

        if (this.activated_challenge?.link_set?.activated_challenge_page_write_link) {
            try{
                const pageLink = topicData.swapUrlForOrgUrl(this.activated_challenge.link_set.activated_challenge_page_write_link);
                setMetaTag('meta[property="og:url"]', 'content', pageLink);
                history.replaceState({}, `${this.activated_challenge.display_name ?? this.activated_challenge?.email?.split('@')[0] ?? ''} - ${this.challenge?.name ?? ''}`, pageLink);
            } catch (e) {
                console.error('Error setting activated challenge page write link', e);
            }
        }

        if (!challengeData.allPlacesHaveBeenVisited()) {
            this.visitedTab = this.remainingOrNextText;
            this.changeCheckingVisitedType({detail: {tabIndex: 0}});
        }

        injectGoogleTagIfConfigured(topicData.topic?.share_settings?.google_tag);

        await this.getRegistrationShareUrl();
    }

    private readonly searchChanged = (value: string) => {
        return debounce(() => this.searchChangedInternal(value ?? ''), 500)();
    };

    get activated_challenge() {
        return challengeData.activated_challenge;
    }

    get challenge() {
        return challengeData.challenge;
    }

    private searchChangedInternal(searchValue: string) {
        const search = searchValue?.trim().toLocaleLowerCase() ?? '';

        if (search !== '') {
            logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.MAP_SEARCH, {
                search_query: search,
            });
            challengeData.applySortFilter({search: [search]});
        }
    }

    updateSection(section: ChallengePageSection) {
        this.current_section = section;
        if (section === 'checkins') {
            this.changeCheckingVisitedType({detail: {tabIndex: 0}});
        }
        if (section === 'leaderboard') {
            void challengeData.refreshActivatedChallenges();
        }
    }

    private get visibleNavSections(): ChallengePageSection[] {
        const sections: ChallengePageSection[] = ['about'];
        if ((challengeData.challengeLocations.length ?? 0) > 0) sections.push('checkins');
        if ((challengeData.challenge?.activities?.length ?? 0) > 0) sections.push('activities');
        if (!challengeData.isType(ChallengeType.Guide)) {
            sections.push('rewards');
            if (challengeData.challenge?.show_leaderboard !== false) sections.push('leaderboard');
            if (challengeData.challenge?.show_photo_feed !== false) sections.push('photo_feed');
        }
        sections.push('share');
        return sections;
    }

    private resolveDefaultSection(): ChallengePageSection {
        const requestedSection = this.mapDefaultTabToSection(challengeData.challenge?.default_tab);
        return this.visibleNavSections.includes(requestedSection)
            ? requestedSection
            : 'about';
    }

    private mapDefaultTabToSection(defaultTab?: ChallengeDefaultTab): ChallengePageSection {
        switch (defaultTab) {
            case ChallengeDefaultTab.Checkins:
                return 'checkins';
            case ChallengeDefaultTab.Activities:
                return 'activities';
            case ChallengeDefaultTab.Rewards:
                return 'rewards';
            case ChallengeDefaultTab.Leaderboard:
                return 'leaderboard';
            case ChallengeDefaultTab.PhotoFeed:
                return 'photo_feed';
            case ChallengeDefaultTab.Share:
                return 'share';
            default:
                return 'about';
        }
    }

    protected updated() {
        if (!this.visibleNavSections.includes(this.current_section)) {
            this.current_section = this.resolveDefaultSection();
        }
    }

    private handleNavTabKeydown(e: KeyboardEvent, section: ChallengePageSection) {
        const sections = this.visibleNavSections;
        const currentIndex = sections.indexOf(section);
        let newIndex = currentIndex;

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            newIndex = (currentIndex + 1) % sections.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            newIndex = (currentIndex - 1 + sections.length) % sections.length;
        } else if (e.key === 'Home') {
            e.preventDefault();
            newIndex = 0;
        } else if (e.key === 'End') {
            e.preventDefault();
            newIndex = sections.length - 1;
        } else {
            return;
        }

        this.updateSection(sections[newIndex]);
        void this.updateComplete.then(() => {
            const nav = this.shadowRoot?.querySelector('nav[role="tablist"]');
            const tabs = nav?.querySelectorAll('[role="tab"]');
            (tabs?.[newIndex] as HTMLElement)?.focus();
        });
    }

    filterVisited() {
        challengeData.applySortFilter({checked_in: true});
    };

    filterNotVisited() {
        challengeData.applySortFilter({checked_in: false});
    };

    filterAll() {
        challengeData.applySortFilter({checked_in: 'all'});
    }

    async getRegistrationShareUrl(): Promise<string> {

        if (isDefined(this.shareUrl)) {
            return this.shareUrl;
        }

        let unique_referral_code: string | undefined = this.activated_challenge?.referral_code;

        if (!unique_referral_code && isDefined(this.activated_challenge)) {
            const activatedChallengeId: string | undefined = challengeData.activated_challenge?._id.$oid;
            const activatedChallengeKey: string = topicData.activatedChallengeKey ?? '';

            if (!activatedChallengeId) {
                getMainPage().errorToastElement.showToast('Activated challenge ID is required for referral code generation');
                return await challengeData.getRegistrationLink();
            }

            unique_referral_code = await getOrCreateActivatedChallengeReferralCode(
                activatedChallengeId,
                activatedChallengeKey
            );

            if (isDefined(this.activated_challenge)) {
                this.activated_challenge.referral_code = unique_referral_code;
            }
        }

        // Build registration URL with referral tracking
        const shareUrl: string = await challengeData.getRegistrationLink();
        if (unique_referral_code) {
            this.shareUrl = `${shareUrl}&ref=${unique_referral_code}`;
        } else {
            this.shareUrl = shareUrl;
        }
        return this.shareUrl;
    }

    async sortNearby() {
        getMainPage().errorToastElement.showToast('Sorting places by distance to you...');
        this.startGeolocationLoop();
        // wait up to three seconds for geolat and geolong to be defined and not 0
        const startTime = Date.now();
        const timeout = 10000;
        const interval = setInterval(async () => {
            if (this.geolat !== 0 && this.geolng !== 0) {
                clearInterval(interval);
                await challengeData.getLocationsDistanceFromUser({ lat: this.geolat, lng: this.geolng });
                this.filterNearbyLocations();
            } else if (Date.now() - startTime > timeout) {
                clearInterval(interval);
                getMainPage().errorToastElement.showToast('Could not get your location. Please try again later.');
            }
        }, 100);
    };

    filterNearbyLocations() {
        challengeData.applySortFilter({nearby_sort: 'asc'});
    }

    async openAwardAnnouncementModal(awards: ChallengeReward[]) {
        this.settingDrawer = 'New Award';
        this.newAwards = awards;
        await this.settingDrawerElement?.open();
        if (!this.settingDrawerElement?.isExpanded) {
            this.settingDrawerElement?.toggleExpand();
        }
    }

    async closeAwardAnnouncementModal() {
        await this.settingDrawerElement?.close();
        this.settingDrawer = undefined;
        this.newAwards = [];
    }

    private startGeolocationLoop() {
        if (!this.geolocationLoopStarted) {
            this.geolocationLoopStarted = true;
            navigator.geolocation.watchPosition((geo) => {
                const lat = geo.coords.latitude;
                const lng = geo.coords.longitude;

                this.geolat = lat;
                this.geolng = lng;
                pointCache.userLocation = { lat, lng };
            });
        }
    }

    private changeActivityCompletedType(e: CustomEvent | any) {
        if (this.allActivitiesHaveBeenCompleted) {
            this.activityTab = 'completed';
            this.filterVisited();
            return;
        }
        if (e.detail.tabIndex === 0) {
            this.activityTab = 'remaining';
            this.filterNotVisited();
        } else {
            this.activityTab = 'completed';
            this.filterVisited();
        }
        this.requestUpdate();
    }

    private changeCheckingVisitedType(e: CustomEvent | any) {

        if (challengeData.allPlacesHaveBeenVisited()) {
            this.visitedTab = this.isVisitedOrLocatedText;
            this.filterVisited();
            this.resetPagination();
            return;
        }
        if (e.detail.tabIndex === 0) {
            this.visitedTab = this.remainingOrNextText;
            if (challengeData.isType(ChallengeType.Guide)) {
                this.filterAll();
            } else {
                this.filterNotVisited();
            }
        } else if (e.detail.tabIndex === 1 && this.challenge?.challenge_type === 'guide') {
            this.visitedTab = 'My List';
        } else {
            this.visitedTab = this.isVisitedOrLocatedText;
            this.filterVisited();
        }
        this.resetPagination();
        this.requestUpdate();
    }

    private resetPagination() {
        this.maxLoad = this.ITEMS_PER_PAGE;
        this.isLoadingMore = false;
        this.hasMoreItems = this.getTotalAvailableItems() > this.maxLoad;
    }

    get userPosition(): number | 'hidden' | undefined {
        // If the current user is hidden from the leaderboard, surface that
        // explicitly so the UI can show "Hidden" instead of a misleading rank.
        if (this.activated_challenge?.hidden_from_leaderboard === true) {
            return 'hidden';
        }

        const sortedChallenges = [...challengeData.activated_challenges]
            .filter(activatedChallenge => !activatedChallenge.hidden_from_leaderboard)
            .sort((a, b) => (b.point_count ?? 0) - (a.point_count ?? 0));

        if (sortedChallenges.length === 0) return undefined;

        let rank = 1;
        let previousPoints = sortedChallenges[0]?.point_count ?? 0;

        for (let i = 0; i < sortedChallenges.length; i++) {
            if (sortedChallenges[i].point_count !== previousPoints) {
                rank = i + 1;
                previousPoints = sortedChallenges[i].point_count;
            }
            if (sortedChallenges[i]._id.$oid === this.activated_challenge?._id.$oid) {
                return rank;
            }
        }
        return sortedChallenges.length + 1; // If the user is not found, return a rank beyond the last position
    }

    private async showRewardDetails(reward: ChallengeReward) {
        const filteredTotals = !reward.selected_category_or_tag_id ? challengeData.challengeLocations : challengeData.challengeLocations.filter(location => location.point_info?.topic_category_id.$oid === reward.selected_category_or_tag_id?.$oid || location.point_info?.tag_info?.user_tags.some(tag => tag._id?.$oid === reward.selected_category_or_tag_id?.$oid));
        const totalPoints = filteredTotals?.reduce((acc, location) => acc + (location.challenge_location_info?.value ?? 0), 0) ?? 0;
        const totalPlaces = filteredTotals?.length ?? 0;
        const pointsRemaining = (reward.reward_criteria_threshold === 0 ? totalPoints : reward.reward_criteria_threshold) - (this.activated_challenge?.point_count ?? 0);
        const placesRemaining = (reward.reward_criteria_threshold === 0 ? totalPlaces : reward.reward_criteria_threshold) - (this.activated_challenge?.checkin_count ?? 0);
        const visitsRemaining = reward.selected_location_id ? (reward.reward_criteria_threshold === 0 ? 1 : reward.reward_criteria_threshold) - (this.activated_challenge?.counted_checkins?.filter(checkin => checkin.location_id.$oid === reward.selected_location_id?.$oid).length ?? 0) : 0;
        const remaining = reward.reward_criteria === 'number_of_places' ? placesRemaining : reward.reward_criteria === RewardCriteria.NumberOfPoints ? pointsRemaining : visitsRemaining;
        this.focusedAward = reward;
        this.pointsToAward = remaining;
        this.settingDrawer = 'Reward Details';
        await this.settingDrawerElement?.open();
    }

    private async closeRewardDetails() {
        await this.settingDrawerElement?.close();
        this.settingDrawer = undefined;
        this.focusedAward = undefined;
        this.pointsToAward = 0;
    }

    private async showLocationDetails(location: ChallengeLocationSummary) {
        this.focusedClue = undefined;
        if (location.point_info === undefined) {
            return;
        }
        this.focusedLocation = location;
        if (this.inner_content_only && !detectMobileViewport()) {
            const marker = pointCache.getMarker(location.point_info._id.$oid);
            if (marker) {
                await topicData.mapComponent?.onClickPoint(location.point_info);
            }
        } else {
            this.settingDrawer = 'Location Details';
            await this.settingDrawerElement?.open();
            if (!this.settingDrawerElement?.isExpanded) {
                // Location Details holds the full point card (image +
                // scrollable details) — open it expanded so the details
                // aren't crushed under the image.
                this.settingDrawerElement?.toggleExpand();
            }
        }
    }

    async showClueDetails(challenge_location_id: string) {
        const location = challengeData.challengeLocations.find(location => location.challenge_location_info?._id?.$oid === challenge_location_id);
        if (!location) {
            console.error('Could not find location with id', challenge_location_id);
            return;
        }

        this.focusedLocation = undefined;
        this.focusedClue = location;
        this.settingDrawer = 'Clue Details';

        // Force close if already open to ensure clean state
        if (this.settingDrawerElement?.isOpened) {
            await this.settingDrawerElement?.close();
        }

        // If element doesn't exist yet, wait a bit and try again
        if (!isDefined(this.settingDrawerElement)) {
            await new Promise(resolve => setTimeout(resolve, 100));
            this.requestUpdate();
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        await this.settingDrawerElement?.open();

        if (this.settingDrawerElement?.isExpanded) {
            // we don't want it to be expanded by default for clues
            this.settingDrawerElement?.toggleExpand();
        }

        this.requestUpdate();

    }

    async closeLocationDetails() {
        await this.settingDrawerElement?.close();
        this.settingDrawer = undefined;
        this.focusedLocation = undefined;
    }

    private async openActivityCheckin(activity: ChallengeActivity) {
        this.settingDrawer = 'Activity Submission';
        this.focusedActivity = activity;
        await this.settingDrawerElement?.open();
        if (!this.settingDrawerElement?.isExpanded) {
            this.settingDrawerElement?.toggleExpand();
        }
    }

    private async closeActivityCheckin(e: CustomEvent) {
        const newAwards = e.detail.newAwards ?? [] as ChallengeReward[];
        await this.settingDrawerElement?.close();
        this.settingDrawer = undefined;
        this.focusedActivity = undefined;

        if (newAwards.length > 0) {
            await this.openAwardAnnouncementModal(newAwards);
        }

        try {
            void show_confetti([topicData.backgroundColor, topicData.textColor]);
        } catch (e) {
            console.error('Error starting Chameleon experience', e);
        }

        await challengeData.refreshChallenge();
        await challengeData.setChallengeLocations();
    }

    async showLocationConfirmCheckin(e: CustomEvent | undefined = undefined) {
        const location_id = e?.detail.challenge_location_id;
        if (isDefined(location_id)) {
            this.focusedClue = challengeData.challengeLocations.find(location => location.challenge_location_info?._id?.$oid === location_id);
            this.focusedLocation = undefined;
        }
        this.settingDrawer = 'Check In';
        await this.settingDrawerElement?.open();
    }

    async giveUpLocation() {
        await challengeData.refreshChallenge();
        await challengeData.setChallengeLocations();
        challengeData.applySortFilter({checked_in: false});
    }

    async showGeneralConfirmCheckin() {
        this.settingDrawer = 'Check In';
        this.focusedActivity = undefined;
        this.focusedLocation = undefined;
        await this.settingDrawerElement?.open();
    }

    async openMap() {
        let map = this.activated_challenge?.link_set?.activated_challenge_map_write_link;
        if (map) {
            map = topicData.swapUrlForOrgUrl(map);
            // open in same tab
            window.open(map, '_self');
        }
    }

    async closeConfirmCheckin(e: CustomEvent) {
        const challenge_location = e.detail.location;
        const newAwards = e.detail.newAwards ?? [] as ChallengeReward[];
        this.settingDrawer = 'Location Details';
        this.checkInComponent?.qrScanner?.stop();
        await this.settingDrawerElement?.close();
        this.requestUpdate();
        try {
            void show_confetti([topicData.backgroundColor, topicData.textColor]);
        } catch (e) {
            console.error('Error starting Chameleon experience', e);
        }

        await challengeData.refreshChallenge();
        await challengeData.setChallengeLocations();

        const tab_number = challengeData.isType(ChallengeType.Guide) ? 2 : 1;

        this.changeCheckingVisitedType({detail: {tabIndex: tab_number}});
        this.visitedTab = this.isVisitedOrLocatedText;
        this.requestUpdate();
        if (newAwards.length > 0) {
            await this.openAwardAnnouncementModal(newAwards);
        } else if (isDefined(challenge_location)) {
            await this.showLocationDetails(challenge_location);
            // if hide special offers until checkin is enabled, refresh the current point so the special offer is shown
            if (isDefined(this.challengePointCard)) {
                this.challengePointCard.showSpecialOffer = true;
            }
        }
    }

    async showFilters() {
        this.settingDrawer = 'Filters';
        await this.settingDrawerElement?.open();
    }

    async handleAdvancedSearch(e: CustomEvent) {

        const keywords = e.detail.keywordsList as string[];
        const selectedCategories = e.detail.selectedCategories as string[];
        const selectedTags = e.detail.selectedTags as TagSearchType;

        if (keywords.length > 0) {
            logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.MAP_KEYWORD_FILTER_APPLIED, {
                search_query: keywords.join(' '),
            });
        }

        for (const category of selectedCategories) {
            logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.MAP_CATEGORY_CLICKED, {
                category_name: category,
            });
        }

        challengeData.applySortFilter({
            search: keywords,
            category_names: selectedCategories,
            tags: selectedTags,
        });

        await this.settingDrawerElement?.close();
    }

    clearFilters() {
        challengeData.clearSortFilter();
        if (isDefined(this.searchElement)) {
            this.searchElement.value = '';
        } else if (this.visitedTab === this.isVisitedOrLocatedText) {
            this.changeCheckingVisitedType({detail: {tabIndex: 1}});
        } else {
            this.changeCheckingVisitedType({detail: {tabIndex: 0}});
        }

        this.requestUpdate();
    }

    private async handleQrCodeLink(key: string) {
        // Open up the modal.
        this.current_section = 'checkins';
        this.visitedTab = this.isVisitedOrLocatedText;
        await this.showLocationConfirmCheckin();

        // Spin until it is ready.
        while (!isDefined(this.checkInComponent)) {
            await delay(100);
        }

        // Start the checkin process.
        await this.checkInComponent?.handleSubmitQrCodeCheckIn(key);
        // remove the query parameter from the URL
        const url = new URL(window.location.href);
        url.searchParams.delete('challenge_location_qr_code_key');
        window.history.replaceState({}, '', url.toString());

    }

    @state()
    private translatedLabels: TranslationPhrases = {};

    // Labels rendered through attributes/props (tab options, placeholders, pills) can't use
    // <translated-span>, so they go through the imperative translateFields pattern instead.
    private get labelPhrases(): TranslationPhrases {
        return {
            remainingOrNext: this.remainingOrNextText,
            visitedOrLocated: this.isVisitedOrLocatedText,
            myList: 'My List',
            remaining: 'Remaining',
            completed: 'Completed',
            search: 'Search',
            pendingApproval: 'Pending Approval',
            awaitingApproval: 'Awaiting Approval',
            hintUsed: 'Hint Used',
            gaveUp: 'Gave Up',
            share: 'Share',
            moreOptions: 'More options',
            or: 'or',
            rewardDetails: 'Reward Details',
            locationDetails: 'Location Details',
            activitySubmission: 'Activity Submission',
            newAward: 'New Award',
            clueDetails: 'Clue Details',
            zoneInformation: 'Stop Information',
            filters: 'Filters',
        };
    }

    private async translateLabels() {
        this.translatedLabels = await translationData.translateFields(this.labelPhrases);
    }

    get remainingOrNextText() {
        if (challengeData.isType(ChallengeType.Treasure)) {
            return 'Remaining Stops';
        }
        if (this.challenge?.challenge_type !== 'guide') {
            return 'Remaining';
        }
        return 'Places';
    }

    get isVisitedOrLocatedText() {
        if (challengeData.isType(ChallengeType.Treasure)) {
            return 'Collected Stops';
        }
        if (challengeData.displayOneAtATime) {
            return 'Completed';
        } else {
            return 'Visited';
        }
    }

    get descriptionString(): string | undefined {
        if (isValidString(this.challenge?.description) && isDefined(this.challenge?.description)) {
            return parseDescriptionText(this.challenge?.description ?? '', false , false);
        }
        return undefined;
    }

    async copyShareUrl() {
        try {
            const shareUrl = this.shareUrl ?? await this.getRegistrationShareUrl();
            await navigator.clipboard.writeText(shareUrl);

            // Show success feedback
            getMainPage().errorToastElement.showToast('Link copied to clipboard!');

        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            getMainPage().errorToastElement.showToast('Failed to copy link');
        }
    }

    async shareNative() {
        const shareUrl = this.shareUrl ?? await this.getRegistrationShareUrl();
        const challengeName = challengeData.challenge?.name ?? 'this challenge';
        const shareText = `Join me in ${challengeName}!`;

        // Check if native sharing is available (mobile devices)
        if ('share' in navigator && detectMobileViewport()) {
            try {
                await navigator.share({
                    title: `Register: ${challengeName}`,
                    text: shareText,
                    url: shareUrl,

                });

            } catch (error) {
                if (error instanceof Error && error.name !== 'AbortError') {
                    console.error('Native share failed:', error);
                    this.fallbackShare(shareUrl, shareText);
                }
            }
        } else {
            // Fallback for desktop or unsupported browsers
            this.fallbackShare(shareUrl, shareText);
        }
    }

    fallbackShare(shareUrl: string, shareText: string) {
        // Copy to clipboard as fallback
        void this.copyShareUrl();

        getMainPage().errorToastElement.showToast('Link copied! Share it however you like.');
    }

    get isFullyLoaded() {
        return challengeData.isReady &&
               isDefined(challengeData.activated_challenge) &&
               isDefined(challengeData.challenge);
    }

    render() {
        if (!this.isFullyLoaded) {
            return html`
            <loading-element style="text-align:center; margin-top:20vh;"
                .loadingString=${'Loading...If this takes more than a few seconds, please try refreshing the page.'}
                ></loading-element>
            `;
        }
        if (this.inner_content_only) {
            return this.renderInnerContent();
        }
        return html`
            <div class="page-wrapper ${detectMobileViewport() ? '' : 'page-wrapper-desktop'}">
                <a href="#main-content" class="skip-link" @click=${(e: Event) => {
                    e.preventDefault();
                    const target = this.shadowRoot?.getElementById('main-content');
                    if (target) {
                        target.setAttribute('tabindex', '-1');
                        target.focus();
                        target.scrollIntoView();
                    }
                }}><translated-span>Skip to main content</translated-span></a>
                <!-- Cover Image and Logo Section (full header) -->
                <header class="cover-section" style="background-color:${topicData.backgroundColor}">
                    ${!this.inner_content_only && (topicData.topic?.translation_languages?.length ?? 0) > 0 ? html`
                        <div class="language-selector-corner"><language-selector></language-selector></div>
                    ` : ''}
                    <div class="image_area">
                        <img .src="${challengeData.challenge?.registration_settings?.cover_image_url ?? topicData.topic?.header_logo ?? ''}" alt="Challenge Logo" class="cover">
                        ${this.challenge?.registration_settings?.logo_url ? html`
                            <img .src="${this.challenge?.registration_settings?.logo_url}" alt="Challenge Logo" class="logo" style="${challengeData.challenge?.registration_settings?.show_logo_background === true ? 'background-color: white;' : 'background-color: transparent;box-shadow: none;'}">
                        ` : ''}
                    </div>
                    <h2 style="color:${topicData.textColor};text-align:center;margin:0.5rem 0 0">${this.challenge?.name ?? 'Challenge Passport'}</h2>
                    ${this.challenge?.challenge_type === 'passport' || challengeData.isType(ChallengeType.Guide) || challengeData.isType(ChallengeType.Treasure) ? html`
                        ${this.activated_challenge?.is_tester === true || challengeData.challenge?.is_live === true ? html`
                            <div class="header-buttons">
                                <form-button size="md" variant="heavy" class="check-in" .backgroundColor=${topicData.textColor} .textColor=${topicData.backgroundColor} @click=${this.openMap}><translated-span>Open Map</translated-span></form-button>
                                ${topicData.hasExceptions === IntegrationException.HighStreetSafari && this.donationAvailableAndUserHasNotPaid ? html`
                                    <form-button size="md" variant="heavy" class="check-in" .backgroundColor=${topicData.textColor} .textColor=${topicData.backgroundColor}
                                        @click=${() => this.updateSection('about')}>
                                        <translated-span>Donate</translated-span>
                                    </form-button>
                                ` : ''}
                            </div>
                        ` : ''}
                    ` : ''}
                </header>
                ${this.renderInnerContent()}

                <footer style="background-color:${topicData.backgroundColor};color:${topicData.textColor}">
                    ${challengeNotStarted(challengeData.challenge, this.activated_challenge?.is_tester) || challengeData.isType(ChallengeType.Guide) ? '' : html`
                        <div class="footer-stats">
                            <div class="stats-text">
                                <p> ${Math.round(challengeData.locationsComplete/challengeData.totalActiveLocations*1.0*100)}% <translated-span>Complete</translated-span></p>
                                <p>${challengeData.totalActiveLocations - challengeData.locationsComplete - challengeData.locationsGivenUp} <translated-span>Remaining</translated-span></p>
                            </div>
                            <progress-element style="width:100%; margin-bottom:0.25rem;" currentIndex="${challengeData.locationsComplete - 1}" totalIndex="${challengeData.totalActiveLocations}" barColor=${palette.successGreen}></progress-element>
                        </div>
                    `}
                    <p style="color:${topicData.textColor}">
                        <translated-span>Viewing as</translated-span> <b>${undefinedIfEmpty(this.activated_challenge?.display_name) ?? this.activated_challenge?.email?.split('@')[0] ?? ''}</b>
                        ${this.challenge?.terms ? html`
                            | <a href="${this.challenge?.terms}" target="_blank" style="color:${topicData.textColor}"><translated-span>Terms and Conditions</translated-span></a> `
                        : ''}
                    </p>

                    <!-- Floating Check-In Button -->
                    ${this.activated_challenge?.is_tester === true || challengeData.challenge?.is_live === true ? html`
                        ${challengeTypeName(this.challenge) !== 'guide' && !this.paymentRequiredAndUserHasNotPaid ? html`
                            ${topicData.hasExceptions === IntegrationException.HighStreetSafari ? html`
                                <!-- High Street Safari: Big orange + button -->
                                <form-button
                                    class="floating-checkin-btn"
                                    size="lg"
                                    style="top: -75px; right: 30px; box-shadow:  2px 4px 8px rgba(0, 0, 0, 0.4); border-radius: 50%;"
                                    variant="circlefab"
                                    icon="add"
                                    borderStyle="round"
                                    .backgroundColor=${'#FF9500'}
                                    .textColor=${'#FFFFFF'}
                                    @click=${this.showGeneralConfirmCheckin}
                                ></form-button>
                            ` : html`
                                <!-- Other challenges: Text button -->
                                <form-button
                                    class="floating-checkin-btn"
                                    size="lg"
                                    variant="heavy"
                                    .backgroundColor="${topicData.backgroundColor}"
                                    .textColor="${topicData.textColor}"
                                    @click=${this.showGeneralConfirmCheckin}
                                >
                                    <translated-span>${undefinedIfEmpty(challengeData.challenge?.button_text) ?? 'Check In'}</translated-span>
                                </form-button>
                            `}
                        ` : ''}
                    `: ''}
                </footer>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    get paymentRequiredAndUserHasNotPaid() {
        if (!challengeData.isPaymentRequired || this.activated_challenge?.is_tester === true) {
            return false;
        }
        if (challengeData.isPaymentRequired) {
            return !(challengeData.thisUserHasPaidSomething() ?? false);
        }
        return false;
    }

    get donationAvailableAndUserHasNotPaid() {
        return challengeData.hasNonRequiredDonationOption && !challengeData.hasMadeDonation;
    }

    get allActivitiesHaveBeenCompleted() {
        return this.challenge?.activities?.every(activity => this.activated_challenge?.activities?.some(activatedActivity => activatedActivity.activity_id.$oid === activity?._id?.$oid && activatedActivity.approved) === true) ?? false;
    }

    async updateSavedLocations(point_id: string) {
        await updateSavedLocation(point_id, challengeData.activatedChallengeId ?? '', challengeData.activatedChallengeKey ?? '');
        await challengeData.setActivatedChallengeForUser();
        this.requestUpdate();
    }

    async getChallengeProducts() {
        this.challengeProducts = await getOrganizationProductsPublic(PaymentTargetType.ActivatedChallenge, challengeData.challenge?._id.$oid ?? '', topicData.topic?.organization_id?.$oid ?? '');
    }

    get visibleProductIds(): string[] {
        const visibleProductIds: string[] = [];
        for (const product of this.challengeProducts) {
            if (product.status !== 'active') {
                continue;
            }
            visibleProductIds.push(product._id.$oid);
        }
        return visibleProductIds;
    }

    async goToPayment() {
        const paymentRequired = this.visibleProductIds.length > 0 && !topicData.isWriter && isDefined(this.preCheckoutElement) && this.preCheckoutElement.remainingLineItems.length > 0;

        if (paymentRequired && isDefined(this.preCheckoutElement)) {
            const email = this.activated_challenge?.email;

            try {
                const response = await startStripeCheckoutSession(email ?? '', this.preCheckoutElement.remainingLineItems, topicData.organizationId ?? '', challengeData.challenge?._id.$oid ?? '', PaymentTargetType.ActivatedChallenge, undefined, this.activated_challenge?._id.$oid, topicData.topicKey, challengeData.activatedChallengeKey);
                // open url in same tab
                if (isDefined(response) && isDefined(response.url)) {
                    window.location.href = response.url;
                }
            } catch (error) {
                getMainPage().errorToastElement.showToast('Could not start the checkout process. You may already be registered. Please contact support.');
            }
        } else {
            getMainPage().errorToastElement.showToast('No payment is required at this time. Please contact support if you believe this is an error.');
        }
    }

    renderInnerContent() {
        const remainingRewards = this.challenge?.rewards.filter(reward =>
            this.activated_challenge?.earned_rewards.some(earnedReward => earnedReward.title === reward.title) !== true
        ) ?? [];
        return html`
            ${this.inner_content_only && challengeData.activated_challenge?.link_set?.activated_challenge_page_write_link ? html`
                <form-button fullWidth size="sm" variant="outline" class="full_challenge_page_link" @click=${() => window.open(topicData.swapUrlForOrgUrl(challengeData.activated_challenge?.link_set?.activated_challenge_page_write_link ?? ''), '_blank')}>
                    <translated-span>Go to Your Participation Page</translated-span>
                </form-button>
            ` : ''}

            ${this.activated_challenge?.is_tester === true ? html`
                <div class="tester-banner">
                    <b><translated-span>Testing Mode</translated-span></b> — <translated-span>you're a tester for this ${challengeTypeName(this.challenge)}, so you can explore and check in before it opens to everyone. Your check-ins count but can be reset when you are ready to go live or when your tester status is removed.</translated-span>
                </div>
            ` : ''}

            ${challengeData.challenge?.is_live === false && !(challengeData.activated_challenge?.is_tester ?? false) ? '' : html`
                <nav class="tabs" role="tablist" aria-label="Challenge sections" style="background-color:${topicData.textColor}; color:${topicData.backgroundColor}">
                        <button role="tab" id="tab-about" aria-selected="${this.current_section === 'about'}" aria-controls="panel-about" tabindex="${this.current_section === 'about' ? 0 : -1}" class="nav-tab ${this.current_section === 'about' ? 'active_tab' : ''}" @click=${() => this.updateSection('about')} @keydown=${(e: KeyboardEvent) => this.handleNavTabKeydown(e, 'about')}><translated-span>About</translated-span></button>
                        ${(challengeData.challengeLocations.length ?? 0) > 0 ? html`
                            <button role="tab" id="tab-checkins" aria-selected="${this.current_section === 'checkins'}" aria-controls="panel-checkins" tabindex="${this.current_section === 'checkins' ? 0 : -1}" class="nav-tab ${this.current_section === 'checkins' ? 'active_tab' : ''}" @click=${() => this.updateSection('checkins')} @keydown=${(e: KeyboardEvent) => this.handleNavTabKeydown(e, 'checkins')}><translated-span>${challengeLocationsDisplayName(challengeData.challenge, true, 'title')}</translated-span></button>
                        ` : ''}
                        ${(challengeData.challenge?.activities?.length ?? 0) > 0 ? html`
                            <button role="tab" id="tab-activities" aria-selected="${this.current_section === 'activities'}" aria-controls="panel-activities" tabindex="${this.current_section === 'activities' ? 0 : -1}" class="nav-tab ${this.current_section === 'activities' ? 'active_tab' : ''}" @click=${() => this.updateSection('activities')} @keydown=${(e: KeyboardEvent) => this.handleNavTabKeydown(e, 'activities')}><translated-span>Activities</translated-span></button>
                        ` : ''}
                        ${!challengeData.isType(ChallengeType.Guide) ? html`
                            <button role="tab" id="tab-rewards" aria-selected="${this.current_section === 'rewards'}" aria-controls="panel-rewards" tabindex="${this.current_section === 'rewards' ? 0 : -1}" class="nav-tab ${this.current_section === 'rewards' ? 'active_tab' : ''}" @click=${() => this.updateSection('rewards')} @keydown=${(e: KeyboardEvent) => this.handleNavTabKeydown(e, 'rewards')}><translated-span>Progress</translated-span></button>
                            ${challengeData.challenge?.show_leaderboard === false ? '' : html`<button role="tab" id="tab-leaderboard" aria-selected="${this.current_section === 'leaderboard'}" aria-controls="panel-leaderboard" tabindex="${this.current_section === 'leaderboard' ? 0 : -1}" class="nav-tab ${this.current_section === 'leaderboard' ? 'active_tab' : ''}" @click=${() => this.updateSection('leaderboard')} @keydown=${(e: KeyboardEvent) => this.handleNavTabKeydown(e, 'leaderboard')}><translated-span>Leaderboard</translated-span></button>`}
                            ${challengeData.challenge?.show_photo_feed === false ? '' : html`<button role="tab" id="tab-photo_feed" aria-selected="${this.current_section === 'photo_feed'}" aria-controls="panel-photo_feed" tabindex="${this.current_section === 'photo_feed' ? 0 : -1}" class="nav-tab ${this.current_section === 'photo_feed' ? 'active_tab' : ''}" @click=${() => this.updateSection('photo_feed')} @keydown=${(e: KeyboardEvent) => this.handleNavTabKeydown(e, 'photo_feed')}><translated-span>Photo Feed</translated-span></button>`}
                        ` : ''}
                        <button role="tab" id="tab-share" aria-selected="${this.current_section === 'share'}" aria-controls="panel-share" tabindex="${this.current_section === 'share' ? 0 : -1}" class="nav-tab ${this.current_section === 'share' ? 'active_tab' : ''}" @click=${() => this.updateSection('share')} @keydown=${(e: KeyboardEvent) => this.handleNavTabKeydown(e, 'share')}><translated-span>Share</translated-span></button>
                </nav>
            `}
            <main id="main-content" class="content_main ${this.inner_content_only ? 'inner_content_main' : ''} ${detectMobileViewport() ? 'content_main_mobile' : ''}">
                <!-- About Tab -->
                <section id="panel-about" role="tabpanel" aria-labelledby="tab-about" ?hidden=${this.current_section !== 'about'}>
                    <div class="business-list scrollable-list" style="flex: 1; overflow-y: auto;height:100%">
                        <div class="card" style="padding: 0;">
                            <div class="text_area" style="text-align: center;gap: 0.5rem;">
                            <h3><translated-span>Welcome to</translated-span> ${this.challenge?.name ?? 'Challenge Passport'}</h3>
                            ${challengeTypeName(this.challenge) !== 'guide' ? html`
                                <span>
                                    ${challengeData.challenge?.start_time ? html`
                                    ${new Date(challengeData.challenge?.start_time).toLocaleDateString()}
                                    ` : ''}
                                    ${challengeData.challenge?.end_time ? html`
                                    -${new Date(challengeData.challenge?.end_time).toLocaleDateString()}
                                    ` : ''}
                                    </p>
                                </span>
                            ` : ''}

                            ${this.donationAvailableAndUserHasNotPaid ? html`
                                <div class="card" style="padding: 1em;">
                                    <pre-checkout-component
                                        id="preCheckout"
                                        style="width: 100%;"
                                        .product_ids=${this.visibleProductIds}
                                        .organization_id=${topicData.topic?.organization_id?.$oid ?? ''}
                                        .edit_view=${true}
                                        .created_item_id=${this.activated_challenge?._id.$oid ?? ''}
                                        .payment_target_type=${PaymentTargetType.ActivatedChallenge}
                                        with_button=${true}
                                        ?minified=${true}
                                        @checkout-clicked=${this.goToPayment}
                                    >
                                    </pre-checkout-component>
                                </div>
                            ` : ''}
                            ${this.challenge?.description ? html`
                                <div style="text-align: left; margin-top: 1em; margin-bottom: 1em;display:block;">
                                    ${html`${unsafeHTML(this.descriptionString)}`}
                                </div>
                            ` : ''}
                        </div>
                    </div>

                    ${detectMobileViewport() && localStorage.getItem('hideHomeScreenPrompt') !== 'true' ? html`
                        <div class="card card-with-border card-with-shadow" style="padding: 1em; width:100%; margin-top: 1em; margin-bottom:1rem">
                            <div style="display: flex; gap: 1em;">
                                ${topicData.topic?.share_settings?.phone_icon_large ?? topicData.topic?.header_logo ? html`
                                    <div style="flex-shrink: 0;">
                                        <img
                                            src="${topicData.topic?.share_settings?.phone_icon_large ?? topicData.topic?.header_logo}"
                                            alt="${this.challenge?.name ?? topicData.topic?.name ?? 'Experience'} icon"
                                            style="width: 60px; height: 60px; border-radius: 12px; object-fit: contain; box-shadow: 0 4px 12px rgba(0,0,0,0.3); background: ${topicData.backgroundColor};margin-bottom: 0.5rem;"
                                        />
                                        <form-button
                                            variant="outline"
                                            size="xs"
                                            @click="${async () => {
                                                this.showHomeScreenVideo = !this.showHomeScreenVideo;
                                                if (this.showHomeScreenVideo) {
                                                    await this.updateComplete.then(() => {
                                                        const videoContainer = this.shadowRoot?.querySelector('.home-screen-video-container');
                                                        videoContainer?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                    });
                                            }}}"
                                            style="margin-top: 0.5rem;"
                                        >
                                            <mwc-icon style="--mdc-icon-size: 1rem; margin-right: 0.25em;" aria-hidden="true">${this.showHomeScreenVideo ? 'videocam_off' : 'videocam'}</mwc-icon>
                                            ${this.showHomeScreenVideo ? html`<translated-span>Hide Video</translated-span>` : html`<translated-span>Video</translated-span>`}
                                        </form-button>
                                    </div>
                                ` : ''}
                                <div style="flex: 1; min-width: 0;">
                                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5em;">
                                        <div style="display: flex; align-items: center; gap: 0.5em;">
                                            <mwc-icon style="--mdc-icon-size: 1rem;" aria-hidden="true">phone_iphone</mwc-icon>
                                            <h3 style="margin: 0; font-size: .9rem; font-weight: 600;"><translated-span>Add to Home Screen</translated-span></h3>
                                        </div>
                                        <mwc-icon-button
                                            icon="close"
                                            @click="${() => setLocalStorage('hideHomeScreenPrompt', 'true')}"
                                            style="--mdc-icon-button-size: 32px; --mdc-icon-size: 20px;"
                                        ></mwc-icon-button>
                                    </div>

                                    <p style="margin: 0; font-size: 0.7rem; opacity: 0.95; line-height: 1.2;">
                                        <translated-span>Get quick access with an app-like experience</translated-span>
                                    </p>

                                    <div style="padding: 0.5em;">
                                        <ol style="margin: 0; padding-left: 1.25em; font-size: 0.7rem; line-height: 1.1;">
                                            <li><translated-span>Tap</translated-span> <mwc-icon style="--mdc-icon-size:0.9rem; vertical-align: middle;" aria-label="${this.translatedLabels.share ?? 'Share'}">ios_share</mwc-icon> ${this.translatedLabels.or ?? 'or'} <mwc-icon style="--mdc-icon-size:0.9rem; vertical-align: middle;" aria-label="${this.translatedLabels.moreOptions ?? 'More options'}">more_vert</mwc-icon> <translated-span>on the top or bottom of your browser (depending on your device).</translated-span></li>
                                            <li><translated-span>Select "Add to Home Screen" or "Add to Device"</translated-span></li>
                                            <li><translated-span>Tap "Add"</translated-span></li>
                                        </ol>
                                    </div>
                                </div>
                            </div>

                            ${this.showHomeScreenVideo ? html`
                                <div class="home-screen-video-container" style="margin-top: 0.75em; border-radius: 6px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.3); aspect-ratio: 9/16; max-height: 400px; max-width: 225px; margin-left: auto; margin-right: auto;">
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        src="${/android/i.test(navigator.userAgent)
                                            ? 'https://www.youtube.com/embed/8uwQc4oPrCc'
                                            : 'https://www.loom.com/embed/00f959f3e41c4f199aaa1993ff585ff2'}"
                                        title="Add to Home Screen Tutorial"
                                        frameborder="0"
                                        webkitallowfullscreen
                                        mozallowfullscreen
                                        allowfullscreen
                                        style="display: block;"
                                    ></iframe>
                                </div>
                            ` : ''}
                        </div>
                    ` : ''}

                    ${challengeData.isType(ChallengeType.Hunt) && (challengeData.activated_challenge?.checkins?.length ?? 0) > 0 ? html`
                        <div class="card" style="padding: 5px;">
                            <form-button variant="outline" size="xs" class="check-in" @click=${this.openMap}>
                                <translated-span>View Completed ${challengeLocationsDisplayName(challengeData.challenge, false, 'title')} Locations on a Map</translated-span>
                            </form-button>
                        </div>
                    ` : ''}
                    </div>
                </section>

                <!-- Check Ins Tab -->
                <section id="panel-checkins" role="tabpanel" aria-labelledby="tab-checkins" ?hidden=${this.current_section !== 'checkins'}>
                    <div class="locations_section">
                         ${this.paymentRequiredAndUserHasNotPaid ? html`
                            <div class="card" style="padding: 1em;">
                                <h3><translated-span>Payment Required</translated-span></h3>
                                <p><translated-span>You must pay to participate in this ${challengeTypeName(this.challenge)}.</translated-span></p>
                                <p><translated-span>Click the button below to pay.</translated-span></p>
                                <pre-checkout-component
                                    id="preCheckout"
                                    .product_ids=${this.visibleProductIds}
                                    .organization_id=${topicData.topic?.organization_id?.$oid ?? ''}
                                    .edit_view=${true}
                                    .created_item_id=${this.activated_challenge?._id.$oid ?? ''}
                                    .payment_target_type=${PaymentTargetType.ActivatedChallenge}
                                >
                                </pre-checkout-component>
                                <form-button variant="outline" size="xs" @click=${this.goToPayment}><translated-span>Pay Now</translated-span></form-button>
                                <span style="color: red; font-size: 0.6rem;"><translated-span>If you have already paid, please refresh the page or reach out to help@proxi.co for help.</translated-span></span>
                            </div>
                        ` : ''}
                        ${challengeNotStarted(this.challenge, this.activated_challenge?.is_tester) && challengeNotLive(this.challenge) ? html`
                            <div class="card" style="padding: 1em; flex: 1 1 auto; min-height: 0; overflow-y: auto;">
                                <h3><translated-span>Experience Not Started</translated-span></h3>
                                <p><translated-span>This ${challengeTypeName(this.challenge)} has not started yet. Check back soon!</translated-span></p>
                                ${this.challenge?.description ? html`
                                    <div style="text-align: left; margin-top: 1em; margin-bottom: 1em;display:block;">
                                        ${html`${unsafeHTML(this.descriptionString)}`}
                                    </div>
                                ` : ''}
                                <br>
                                ${challengeData.isType(ChallengeType.Passport) && challengeData.challengeLocations.length > 0 ? html`
                                    <p><translated-span>Here's what you can look forward to!</translated-span></p>
                                    <div style="flex: 1; overflow-y: auto;text-align: center;">
                                        ${challengeData.challengeLocations.map(location => {
                                            return html`
                                                <span>${location.point_info?.name ?? location.point_info?.location.search}</span><br>
                                            `;
                                        })}
                                    </div>
                                ` : ''}
                            </div>
                        ` : challengeHasEnded(this.challenge, this.activated_challenge?.is_tester) && challengeNotLive(this.challenge) ? html`
                            <div class="card" style="padding: 1em; flex: 1 1 auto; min-height: 0; overflow-y: auto;">
                                ${this.challenge?.description ? html`
                                    <div style="text-align: left; margin-top: 1em; margin-bottom: 1em;display:block;">
                                        ${html`${unsafeHTML(this.descriptionString)}`}
                                    </div>
                                ` : ''}
                                <h3><translated-span>Experience Has Ended</translated-span></h3>
                                <p><translated-span>This ${challengeTypeName(this.challenge)} ended on</translated-span> ${new Date(this.challenge?.end_time ?? '').toLocaleDateString()}.</p>
                                <p><translated-span>Thank you for participating!</translated-span></p>
                                ${challengeData.isType(ChallengeType.Passport) && challengeData.challengeLocations.length > 0 ? html`
                                    <br>
                                    <p><translated-span>Here are all the locations that were part of this ${challengeTypeName(this.challenge)}:</translated-span></p>
                                    <div style="flex: 1; overflow-y: auto;text-align: center;">
                                        ${challengeData.challengeLocations.map(location => {
                                            return html`
                                                <span style="${(location.checked_in ?? false) ? 'font-weight: bold; color: green;' : 'color: #666;'}">
                                                    ${(location.checked_in ?? false) ? '✓ ' : ''}${location.point_info?.name ?? location.point_info?.location.search}
                                                </span><br>
                                            `;
                                        })}
                                    </div>
                                ` : ''}
                            </div>
                            `
                            : challengeNotLive(this.challenge, this.activated_challenge?.is_tester) ? html`
                            <div class="card" style="padding: 1em; flex: 1 1 auto; min-height: 0; overflow-y: auto;">
                                ${this.challenge?.description ? html`
                                    <div style="text-align: left; margin-top: 1em; margin-bottom: 1em;display:block;">
                                        ${html`${unsafeHTML(this.descriptionString)}`}
                                    </div>
                                ` : ''}
                                <h3><translated-span>Experience Not Live</translated-span></h3>
                                <p><translated-span>This ${challengeTypeName(this.challenge)} is not set to live at this time. Please check back later.</translated-span></p>
                            </div>
                            `
                            : challengeData.allPlacesHaveBeenVisited() && !challengeData.isType(ChallengeType.Guide) ? html`
                            <div class="card" style="padding: 1em;">
                                <h3><translated-span>All ${challengeLocationsDisplayName(challengeData.challenge, true, 'title')} Completed</translated-span></h3>
                                <p><translated-span>You have completed all the ${challengeLocationsDisplayName(challengeData.challenge, true, 'lower')} in this ${challengeTypeName(this.challenge)}.</translated-span></p>
                                <p><translated-span>Great job!</translated-span></p>
                            </div>
                        ` : ''}
                        ${challengeData.allPlacesHaveBeenVisited() || challengeNotLive(this.challenge, this.activated_challenge?.is_tester) || challengeHasEnded(this.challenge, this.activated_challenge?.is_tester) || this.paymentRequiredAndUserHasNotPaid ? '' : html`
                            <tabs-component id="tabsComponent"
                                .options=${
                                    challengeData.isType(ChallengeType.Guide) ?
                                        [this.translatedLabels.remainingOrNext ?? this.remainingOrNextText, this.challenge?.challenge_type === 'guide' ? this.translatedLabels.myList ?? 'My List' : '', this.translatedLabels.visitedOrLocated ?? this.isVisitedOrLocatedText] :
                                        [this.translatedLabels.remainingOrNext ?? this.remainingOrNextText, this.translatedLabels.visitedOrLocated ?? this.isVisitedOrLocatedText]
                                }
                                .type=${'pill'}
                                style="width: 100%;"
                                .tabIndex=${
                                    challengeData.isType(ChallengeType.Guide) ?
                                        challengeData.allPlacesHaveBeenVisited()? 2 :
                                        this.visitedTab === this.isVisitedOrLocatedText ? 2 :
                                        this.visitedTab === 'My List' ? 1 :
                                        this.remainingOrNextText === this.visitedTab ? 0 : 0
                                    :
                                        challengeData.allPlacesHaveBeenVisited()? 1 :
                                        this.visitedTab === this.isVisitedOrLocatedText ? 1 :
                                        this.remainingOrNextText === this.visitedTab ? 0 : 0
                                }
                                @on-select-tab=${(e: CustomEvent) => {
                                    this.changeCheckingVisitedType(e);
                                }}
                            ></tabs-component>
                        `}
                        ${challengeNotLive(this.challenge, this.activated_challenge?.is_tester) || challengeHasEnded(this.challenge, this.activated_challenge?.is_tester) || this.paymentRequiredAndUserHasNotPaid ? '' : html`
                            ${challengeData.allPlacesHaveBeenVisited() ? '' : html`
                            <div class="sort_and_filter">
                                ${challengeData.filtersApplied && (challengeData.location_sort_filter.search?.length ?? 0) === 0 ? '' : html`
                                    <proxi-textfield
                                        outlined
                                        id="search"
                                        icon="search"
                                        class="input searchinput"
                                        style="flex: 1;"
                                        placeholder="${this.translatedLabels.search ?? 'Search'}"
                                        @input="${() => this.searchChanged(this.searchElement.value)}">
                                    </proxi-textfield>
                                `}
                                ${(challengeData.location_sort_filter.search?.length ?? 0) > 0 ? '' : html`
                                    <form-button
                                        variant="outline"
                                        icon="near_me"
                                        size="sm"
                                        @click=${this.sortNearby}
                                    ><translated-span>Near Me</translated-span></form-button>
                                    <form-button
                                        variant="outline"
                                        icon="filter_alt"
                                        size="sm"
                                        @click=${this.showFilters}
                                    ><translated-span>Filter</translated-span></form-button>
                                `}
                                ${challengeData.filtersApplied ? html`
                                    <form-button
                                        variant="outline"
                                        icon="clear"
                                        size="sm"
                                        @click=${this.clearFilters}
                                    ><translated-span>Clear Filters</translated-span></form-button>
                                ` : ''}
                            </div>
                            `}

                            ${challengeData.isType(ChallengeType.Passport) || challengeData.isType(ChallengeType.Guide) ? html`
                                <div class="business-list scrollable-list" style="flex: 1; overflow-y: auto;" @scroll=${this.handleScroll}>
                                    ${this.visitedTab === 'My List' ?
                                        this.renderMyList()
                                         : this.renderVisitedOrNotVisited()
                                    }
                                </div>
                            ` : html`
                                <div class="business-list scrollable-list" style="flex: 1; overflow-y: auto;" @scroll=${this.handleScroll}>
                                   ${this.renderClueOrVisited()}
                                </div>
                                ${this.filteredLocations.length === 0 ? html`
                                    <p><translated-span>No ${challengeLocationsDisplayName(challengeData.challenge, true, 'lower')} found</translated-span></p>
                                ` : ''}
                            `}
                        `}
                    </div>
                </section>

                <!-- Activities Tab -->
                <section id="panel-activities" role="tabpanel" aria-labelledby="tab-activities" ?hidden=${this.current_section !== 'activities'}>
                    ${this.paymentRequiredAndUserHasNotPaid ? html`
                        <div class="card" style="padding: 1em;">
                            <h3><translated-span>Payment Required</translated-span></h3>
                            <p><translated-span>You must pay to participate in this ${challengeTypeName(this.challenge)}.</translated-span></p>
                            <p><translated-span>Click the button below to pay.</translated-span></p>
                            <form-button variant="outline" size="xs" @click=${this.goToPayment}><translated-span>Pay Now</translated-span></form-button>
                            <span style="color: red; font-size: 0.6rem;"><translated-span>If you have already paid, please refresh the page or reach out to help@proxi.co for help.</translated-span></span>
                        </div>
                    ` : ''}
                    ${this.allActivitiesHaveBeenCompleted ? html`
                        <div class="card" style="padding: 1em;">
                            <h3><translated-span>All Activities Completed</translated-span></h3>
                            <p><translated-span>Great job! You've completed all the activities.</translated-span></p>
                        </div>
                    ` : ''}
                    <div class="business-list scrollable-list" style="flex: 1; overflow-y: auto;">
                        ${this.allActivitiesHaveBeenCompleted || this.paymentRequiredAndUserHasNotPaid ? '' : html`
                            <tabs-component id="tabsComponent"
                                .options=${[this.translatedLabels.remaining ?? 'Remaining', this.translatedLabels.completed ?? 'Completed']}
                                .type=${'pill'}
                                style="width: 100%;"
                                .tabIndex=${this.allActivitiesHaveBeenCompleted ? 1 : this.activityTab === 'remaining' ? 0 : 1}
                                @on-select-tab=${(e: CustomEvent) => {
                                    this.changeActivityCompletedType(e);
                                }}
                            ></tabs-component>
                        `}
                        ${this.paymentRequiredAndUserHasNotPaid ? '' : html`
                            ${challengeData.challenge?.activities?.map(activity => {
                                const completed = challengeData.activated_challenge?.activities?.find(activatedActivity => activatedActivity.activity_id.$oid === activity?._id?.$oid)?.approved === true;
                                if (this.activityTab === 'remaining' && completed && !(this.allActivitiesHaveBeenCompleted ?? false)) {
                                    return html``;
                                }
                                if (this.activityTab === 'completed' && !completed && !(this.allActivitiesHaveBeenCompleted ?? false)) {
                                    return html``;
                                }
                                return html`
                                    <basic-activity-card
                                        @on-card-click=${challengeData.activated_challenge?.activities?.find(activatedActivity => activatedActivity.activity_id.$oid === activity?._id?.$oid) ? undefined : async () => await this.openActivityCheckin(activity)}
                                        style="width:100%"
                                        .challenge_activity=${activity}
                                        .completed=${challengeData.activated_challenge?.activities?.find(activatedActivity => activatedActivity.activity_id.$oid === activity?._id?.$oid)?.approved === true}
                                        .pillText=${challengeData.activated_challenge?.activities?.find(activatedActivity => activatedActivity.activity_id.$oid === activity?._id?.$oid)?.approved === false ? this.translatedLabels.awaitingApproval ?? 'Awaiting Approval' : undefined}
                                    ></basic-activity-card>
                                `;
                            })
                            }
                        `}
                    </div>
                </section>


                <!-- Rewards Tab -->
                <section id="panel-rewards" role="tabpanel" aria-labelledby="tab-rewards" ?hidden=${this.current_section !== 'rewards'}>
                    <div class="progress">
                        <div class="stats-card__container">
                            <div class="stats-card">
                                <div class="stats-card__value">${challengeData.activated_challenge?.point_count ?? 0}</div>
                                <div class="stats-card__label"><translated-span>Points Earned</translated-span></div>
                            </div>
                            <div class="stats-card">
                                <div class="stats-card__value">${challengeData.activated_challenge?.checkin_count ?? 0}</div>
                                <div class="stats-card__label"><translated-span>Places Visited</translated-span></div>
                            </div>
                            ${(challengeData.challenge?.activities?.length ?? 0) > 0 ? html`
                                <div class="stats-card">
                                    <div class="stats-card__value">${challengeData.activated_challenge?.activities?.filter(activity => activity.approved).length ?? 0}</div>
                                    <div class="stats-card__label"><translated-span>Activities Completed</translated-span></div>
                                </div>
                            ` : ''}
                        </div>
                        ${(this.activated_challenge?.admin_assigned_points?.length ?? 0) > 0 ? html`
                            <br><span><translated-span>Plus</translated-span> <b>${this.activated_challenge?.admin_assigned_points?.map(point => point.value).reduce((a, b) => a + b, 0)}</b> <translated-span>total bonus points for:</translated-span></span>
                                ${this.activated_challenge?.admin_assigned_points?.map(point => html`
                                    <span>${point.description} (${point.value})</span>
                                `)}
                        ` : ''}
                    </div>
                    ${(this.challenge?.rewards.length ?? 0) > 0 ? html`
                        ${(this.activated_challenge?.earned_rewards.length ?? 0) > 0 ? html`
                            <h3><translated-span>Earned Rewards</translated-span></h3>
                            <div class="earned-rewards">
                                ${this.activated_challenge?.earned_rewards?.map(reward => html`
                                    <div class="reward-card card-with-border card-with-shadow" @click=${async () => await this.showRewardDetails(reward)}>
                                        <img src="${reward.image_url}" alt="Reward Icon">
                                        <p style="display: flex; align-items: center; flex-direction: row;">
                                            <b>${reward.title}</b>
                                            ${undefinedIfEmpty(reward.completion_url) && this.pointsToAward < 1 ? html`
                                                <form-button
                                                    variant="outline"
                                                    size="xs"
                                                    icon="open_in_new"
                                                    @click=${() => window.open(reward.completion_url, '_blank')}
                                                ></form-button>
                                                ` : ''}
                                        </p>
                                        ${reward.redeemable && ((this.activated_challenge?.redeemed_rewards?.some(redeemedReward => redeemedReward._id.$oid === reward?._id?.$oid)) ?? false) ? html`
                                            <p><translated-span>Redeemed</translated-span></p>
                                        ` : ''}
                                    </div>
                                `)}
                            </div>
                        ` : ''}
                        ${remainingRewards.length > 0 ? html`
                            <h3><translated-span>Remaining Rewards</translated-span></h3>
                            <div class="earned-rewards">
                                ${remainingRewards.map(reward => {
                                    // Calculate remaining points
                                    return html`
                                        <div class="reward-card card-with-border card-with-shadow" @click=${async () => {await this.showRewardDetails(reward); this.requestUpdate();}}>
                                            <img src="${reward.image_url}" alt="Reward Icon">
                                            <p><b>${reward.title}</b></p>
                                        </div>

                                    `;
                                })}
                            </div>
                        ` : ''}
                    ` : ''}
                </section>
                <!-- Leaderboard Tab -->
                <section id="panel-leaderboard" role="tabpanel" aria-labelledby="tab-leaderboard" ?hidden=${this.current_section !== 'leaderboard'}>
                    <challenge-leaderboard
                        .userPosition=${this.userPosition}
                        .participants=${challengeData.activated_challenges}
                    >
                    </challenge-leaderboard>
                </section>
                <!-- Photo Feed Tab -->
                <section id="panel-photo_feed" role="tabpanel" aria-labelledby="tab-photo_feed" ?hidden=${this.current_section !== 'photo_feed'}>
                    <challenge-photo-feed
                    ></challenge-photo-feed>
                </section>

                <!-- Share Tab -->
                <section id="panel-share" role="tabpanel" aria-labelledby="tab-share" ?hidden=${this.current_section !== 'share'}>
                    <div class="card" style="padding: 20px;text-align:center;">
                        <p style="margin-bottom: 20px; color: #666;"><translated-span>Share this ${challengeTypeName(this.challenge)} with others! They can join using the registration link below!</translated-span></p>

                        <div style="margin-bottom: 20px;">
                            <label style="display: block; margin-bottom: 8px; font-weight: bold;"><translated-span>Registration Link:</translated-span></label>
                            <copy-link-component
                                text="${this.shareUrl ?? ''}"
                                successMessage="Link copied successfully."
                                textType="Share">
                                <translated-span>Copy Link</translated-span>
                            </copy-link-component>
                        </div>

                        <div style="display: flex; gap: 10px; flex-wrap: wrap;">
                            <form-button
                                variant="primary"
                                size="md"
                                @click=${this.shareNative}
                            ><translated-span>Share ${challengeTypeName(this.challenge)}</translated-span></form-button>
                        </div>
                    </div>
                </section>

                <!-- Account Tab -->
                <section id="account" role="tabpanel" aria-label="Account" ?hidden=${this.current_section !== 'account'}>
                    <h2>Account</h2>
                    <form>
                        <label for="name">Name</label>
                        <input type="text" id="name" name="name" value="John Doe">

                        <label for="email">Email</label>
                        <input type="email" id="email" name="email" value="john.doe@example.com">

                        <button type="submit">Update</button>
                    </form>
                    <button class="support-button">Support</button>
                </section>
            </main>
            <setting-drawer id="settingDrawer" .title="${this.settingDrawerTitle}" @on-close=${this.closeRewardDetails} .noHeader=${this.settingDrawer === 'Location Details'} .isExpanded=${this.settingDrawer === 'Location Details'}>
                ${this.settingDrawer === 'Reward Details' && this.focusedAward ? html`
                    <div class="reward-details">
                        <h2>${this.focusedAward.title}</h2>
                        <img src="${this.focusedAward.image_url}" alt="Reward Icon">
                        <p>${this.focusedAward.description}</p>
                        <p><translated-span>Criteria:</translated-span> ${this.focusedAward.reward_criteria === RewardCriteria.VerifiedSpend
                                ? html`<translated-span>Spend</translated-span> ${formatPrice(this.focusedAward.reward_criteria_threshold, this.challenge?.receipt_settings?.currency ?? 'USD')}`
                                : html`${this.focusedAward.reward_criteria_threshold === 0 ? 'all' : this.focusedAward.reward_criteria_threshold} ${this.focusedAward.reward_criteria === 'number_of_places' ? 'places' : this.focusedAward.reward_criteria === 'number_of_points' ? 'points' : 'visits'}`}
                            ${this.focusedAward.selected_category_or_tag_id ? `in ${topicData.categoryOrTagNameById(this.focusedAward?.selected_category_or_tag_id?.$oid) ?? ''}` : ''}
                        </p>
                        ${this.pointsToAward > 0 ? html`
                            <p>${this.focusedAward.reward_criteria === 'number_of_places' || this.focusedAward.reward_criteria === RewardCriteria.VisitsToLocation ? 'Visit' : 'Earn'}
                                ${this.focusedAward.reward_criteria === RewardCriteria.VisitsToLocation ? challengeData.challengeLocations.find(location => location.challenge_location_info?._id?.$oid === this.focusedAward?.selected_location_id?.$oid)?.point_info?.name ?? '' : ''}
                                ${this.pointsToAward}
                                more ${this.focusedAward.reward_criteria === 'number_of_places' ? 'places' : this.focusedAward.reward_criteria === 'number_of_points' ? 'points' : 'times'}
                            ${this.focusedAward.selected_category_or_tag_id ? `in ${topicData.categoryOrTagNameById(this.focusedAward?.selected_category_or_tag_id?.$oid) ?? ''}` : ''}

                            to collect this reward.</p>
                        ` : ''}
                        ${this.focusedAward.redeemable && ((this.activated_challenge?.redeemed_rewards?.some(reward => reward._id.$oid === this.focusedAward?._id?.$oid)) ?? false) ? html`
                            <p><translated-span>This reward was redeemed on</translated-span> ${new Date(this.activated_challenge?.redeemed_rewards?.find(reward => reward._id.$oid === this.focusedAward?._id?.$oid)?.timestamp ?? '').toLocaleDateString()}</p>
                        ` : ''}

                        ${this.focusedAward.completion_message && this.pointsToAward < 1 ? html`
                            <p>${this.focusedAward.completion_message}</p>
                        ` : ''}
                        ${undefinedIfEmpty(this.focusedAward.completion_url) && this.pointsToAward < 1 ? html`
                            <form-button
                                variant="primary"
                                size="sm"
                                class="claim-reward-button"
                                @click=${() => window.open(this.focusedAward?.completion_url, '_blank')}
                            ><translated-span>Reward Link</translated-span></form-button>
                        ` : ''}
                    </div>
                ` : ''}

                ${this.settingDrawer === 'Location Details' && this.focusedLocation ? html`
                    <challenge-point-card
                        id="challengePointCard"
                        .point=${this.focusedLocation.point_info}
                        .challengePageView=${true}
                        .isCheckedin=${this.focusedLocation.checked_in ?? false}
                        .showSpecialOffer=${shouldRevealSpecialOffer({
                            challenge: this.challenge,
                            isCheckedIn: this.focusedLocation.checked_in === true,
                            isGuide: challengeData.isType(ChallengeType.Guide),
                        })}
                        .challenge_points=${challengeData.isType(ChallengeType.Guide) ? undefined : this.focusedLocation.checked_in === true || this.focusedLocation.gave_up === true ? this.focusedLocation.earned_points : this.focusedLocation.challenge_location_info?.value}
                        .clue_details=${this.focusedLocation.challenge_location_info?.clue_details}
                        .is_saved_point=${this.activated_challenge?.personal_locations?.saved?.map(id => id.$oid).includes(this.focusedLocation.point_info?._id?.$oid ?? '') === true}
                        @on-save-point=${async () => {await this.updateSavedLocations(this.focusedLocation?.point_info?._id.$oid ?? '');}}
                    >
                    </challenge-point-card>
                ` : ''}
                ${this.settingDrawer === 'Clue Details' && this.focusedClue ? html`
                    <clue-details-point-card
                        id="challengePointCard"
                        .clueLocation=${this.focusedClue}
                        .challengePageView=${true}
                        .challenge_points=${this.focusedClue?.challenge_location_info?.value}
                        .point=${undefined}
                        @checkin=${async (e: CustomEvent) => await this.showLocationConfirmCheckin(e)}
                        @giveup=${this.closeLocationDetails}
                    >
                    </clue-details-point-card>
                ` : ''}
                ${this.settingDrawer === 'Check In' ? html`
                    <checkin-challenge-component
                        class="inner_desktop_drawer_content"
                        id="challengeCheckIn"
                        .open="${true}"
                        .pendingCheckinPoint="${this.focusedLocation?.point_info}"
                        .pendingCheckinClue="${this.focusedClue}"
                        .showEmailConfirm="${false}"
                        @close-after-checkin="${this.closeConfirmCheckin}"
                    ></checkin-challenge-component>
                ` : ''}
                ${this.settingDrawer === 'Filters' ? html`
                    <advanced-filtering-modal
                        .challengeFilter=${true}
                        .selectedTags="${challengeData.location_sort_filter.tags ??  { operator: CombineOperator.Or, tags: []}}"
                        .selectedCategories=${challengeData.location_sort_filter.category_names ?? []}
                        .keywordsList=${challengeData.location_sort_filter.search ?? []}
                        @clear-all-filters=${this.clearFilters}
                        @on-advanced-search=${this.handleAdvancedSearch}>

                    </advanced-filtering-modal>
                ` : ''}
                ${this.settingDrawer === 'Activity Submission' && this.focusedActivity ? html`
                    <checkin-challenge-component
                        class="inner_desktop_drawer_content"
                        id="challengeCheckIn"
                        .open="${true}"
                        .pendingCheckinPoint=${undefined}
                        .pendingCheckinClue=${undefined}
                        .pendingCheckinActivity=${this.focusedActivity}
                        .showEmailConfirm="${false}"
                        @close-after-checkin="${this.closeActivityCheckin}"
                    ></checkin-challenge-component>
                ` : ''}
                ${this.settingDrawer === 'New Award' && this.newAwards.length > 0 ? html`
                    <challenge-award-announcement
                        .challengeReward="${this.newAwards}"
                    ></challenge-award-announcement>
                ` : ''}
            </setting-drawer>

        `;
    }

    get settingDrawerTitle() {
        if (this.settingDrawer === 'Check In') {
            return this.challenge?.button_text ?? 'Check In';
        }
        if (this.settingDrawer === 'Clue Details' && challengeData.isType(ChallengeType.Treasure)) {
            return this.translatedLabels.zoneInformation ?? 'Stop Information';
        }
        switch (this.settingDrawer) {
            case 'Reward Details': return this.translatedLabels.rewardDetails ?? 'Reward Details';
            case 'Location Details': return this.translatedLabels.locationDetails ?? 'Location Details';
            case 'Activity Submission': return this.translatedLabels.activitySubmission ?? 'Activity Submission';
            case 'New Award': return this.translatedLabels.newAward ?? 'New Award';
            case 'Clue Details': return this.translatedLabels.clueDetails ?? 'Clue Details';
            case 'Filters': return this.translatedLabels.filters ?? 'Filters';
            default: return this.settingDrawer ?? '';
        }
    }

    renderMyList() {
        const places = this.activated_challenge?.personal_locations?.saved ?? [];
        const placesToLoad = places.slice(0, this.maxLoad);
        return html`
            ${placesToLoad.map(id => {
                const location = challengeData.challengeLocations.find(loc => loc.point_info?._id?.$oid === id.$oid);
                if (!location) {
                    return '';
                }
                return html`
                    <basic-point-card
                        @on-card-click=${async () => await this.showLocationDetails(location)}
                        style="width:100%"
                        .challenge_points=${challengeData.isType(ChallengeType.Guide) ? undefined : location.checked_in === true || location.gave_up === true ? location.earned_points : location.challenge_location_info?.value}
                        .point=${location.point_info}
                        @on-save-point=${async () => {await this.updateSavedLocations(location.point_info?._id.$oid ?? '');}}
                        .is_saved_point=${this.activated_challenge?.personal_locations?.saved?.map(id => id.$oid).includes(location.point_info?._id?.$oid ?? '') === true}
                        .subtext=${location.point_info?.location.search}
                        .geo_location_eligible=${location.geo_location_eligible ?? false}
                        .checked_in=${location.checked_in ?? false}
                        .pillText=${this.activated_challenge?.pending_checkin_location_ids?.includes(location.challenge_location_info?._id?.$oid ?? '') === true ? this.translatedLabels.pendingApproval ?? 'Pending Approval' : undefined}
                    ></basic-point-card>
                `;
                })

            }
            ${this.renderLoadingIndicator()}
            ${this.renderEndMessage()}
        `;
    }

    renderClueOrVisited() {
        const placesToLoad = this.filteredLocations.slice(0, this.maxLoad);
        return html`
            ${placesToLoad?.map(location => {
                const used_hint = challengeData.activated_challenge?.scavenger_hunt_use?.hint_location_ids?.map(id => id.$oid).includes(location.challenge_location_info?._id?.$oid ?? '') ?? false;
                const onlyShowFirstClue = this.challenge?.scavenger_hunt_settings?.display_one_clue_at_a_time === true;
                const isTreasureHuntandNotCheckedIn = challengeData.isType(ChallengeType.Treasure) && !(location.checked_in ?? true);
                if (!location.challenge_location_info?.location || (topicData.isWriter && !(location.checked_in ?? true)) || isTreasureHuntandNotCheckedIn) {
                    if (onlyShowFirstClue && location !== challengeData.nextUnCheckedInLocation) {
                        return html``;
                    }
                    return html`
                        <clue-point-card
                            @on-card-click=${async () => await this.showClueDetails(location?.challenge_location_info?._id?.$oid ?? '')}
                            style="width:100%"
                            .challenge_points=${location.challenge_location_info?.value}
                            .clueDetails=${location?.challenge_location_info?.clue_details}
                            .geo_location_eligible=${false}
                            .pillText=${used_hint ? this.translatedLabels.hintUsed ?? 'Hint Used' : undefined}
                        ></clue-point-card>
                    `;
                }
                const hasGiveUp = challengeData.activated_challenge?.scavenger_hunt_use?.give_up_location_ids?.map(id => id.$oid).includes(location.challenge_location_info?._id?.$oid ?? '');
                    return html`
                        <basic-point-card
                            @on-card-click=${async () => await this.showLocationDetails(location)}
                            style="width:100%"
                            .challenge_points=${location.checked_in === true || location.gave_up === true ? location.earned_points : location.challenge_location_info?.value}
                            .point=${location.point_info}
                            .subtext=${location.challenge_location_info?.clue_details?.clue}
                            .geo_location_eligible=${location.geo_location_eligible ?? false}
                            .checked_in=${location.checked_in ?? false}
                            .pillText=${this.activated_challenge?.pending_checkin_location_ids?.includes(location.challenge_location_info?._id?.$oid ?? '') === true ? this.translatedLabels.pendingApproval ?? 'Pending Approval' : hasGiveUp === true ? this.translatedLabels.gaveUp ?? 'Gave Up' : used_hint ? this.translatedLabels.hintUsed ?? 'Hint Used' : undefined}
                        ></basic-point-card>
                    `;
            }
            )}
            ${this.renderLoadingIndicator()}
            ${this.renderEndMessage()}
        `;
    }

    renderVisitedOrNotVisited() {
        const placesToLoad = this.filteredLocations.slice(0, this.maxLoad);
        return html `
            ${placesToLoad?.map(location => {
                return html`
                    <basic-point-card
                        @on-card-click=${async () => await this.showLocationDetails(location)}
                        style="width:100%"
                        .challenge_points=${challengeData.isType(ChallengeType.Guide) ? undefined : location.checked_in === true || location.gave_up === true ? location.earned_points : location.challenge_location_info?.value}
                        .point=${location.point_info}
                        .subtext=${location.point_info?.location.search}
                        @on-save-point=${async () => {await this.updateSavedLocations(location.point_info?._id.$oid ?? '');}}
                        .is_saved_point=${this.activated_challenge?.personal_locations?.saved?.map(id => id.$oid).includes(location.point_info?._id?.$oid ?? '') === true}
                        .geo_location_eligible=${location.geo_location_eligible ?? false}
                        .checked_in=${location.checked_in ?? false}
                        .pillText=${this.activated_challenge?.pending_checkin_location_ids?.includes(location.challenge_location_info?._id?.$oid ?? '') === true ? this.translatedLabels.pendingApproval ?? 'Pending Approval' : undefined}
                    ></basic-point-card>
                `;
            })
            }
            ${this.renderLoadingIndicator()}
            ${this.renderEndMessage()}
            ${this.filteredLocations.length === 0 ? html`
                <p>${challengeNotStarted(this.challenge, this.activated_challenge?.is_tester) || challengeData.allPlacesHaveBeenVisited() ? '' : html`<translated-span>No locations found</translated-span>`}</p>
            ` : ''}
        `;
    }

    private renderLoadingIndicator() {
        if (!this.isLoadingMore) {
            return '';
        }

        return html`
            <div class="loading-more">
                <loading-element .loadingString=${'Loading more items...'}></loading-element>
            </div>
        `;
    }

    private renderEndMessage() {
        if (this.hasMoreItems || this.isLoadingMore) {
            return '';
        }

        return html`
            <div class="end-of-list">
                <p><translated-span>You've reached the end of the list</translated-span></p>
            </div>
        `;
    }

    private readonly handleScroll = (e: Event) => {
        const target = e.target as HTMLElement;
        if (!isDefined(target) || this.isLoadingMore || !this.hasMoreItems) {
            return;
        }

        // Check if user has scrolled to near the bottom
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // Trigger load when within 100px of bottom
        if (scrollTop + clientHeight >= scrollHeight - 100) {
            this.loadMoreItems();
        }
    };

    private loadMoreItems(): void {
        if (this.isLoadingMore || !this.hasMoreItems) {
            return;
        }

        this.isLoadingMore = true;

        const currentMax = this.maxLoad;
        const totalAvailable = this.getTotalAvailableItems();

        // Guard against sections with no paginated items (e.g. About tab)
        if (totalAvailable === 0) {
            this.isLoadingMore = false;
            return;
        }

        // Guard against sections with no paginated items (e.g. About tab)
        if (totalAvailable === 0) {
            this.isLoadingMore = false;
            return;
        }

        // Increase maxLoad by ITEMS_PER_PAGE
        this.maxLoad = Math.min(currentMax + this.ITEMS_PER_PAGE, totalAvailable);

        // Check if we've loaded all items
        if (this.maxLoad >= totalAvailable) {
            this.hasMoreItems = false;
        }

        this.isLoadingMore = false;
        this.requestUpdate();
    }

    private getTotalAvailableItems(): number {
        // Return total items based on current tab
        if (this.current_section === 'checkins') {
            if (this.visitedTab === 'My List') {
                return this.activated_challenge?.personal_locations?.saved?.length ?? 0;
            }
            return this.filteredLocations.length;
        } else if (this.current_section === 'activities') {
            return this.challenge?.activities?.length ?? 0;
        }
        // Add other sections as needed
        return 0;
    }

    static get styles() {
        return css`
            .tester-banner {
                background-color: #F1E8C3;
                color: #1E1B2C;
                padding: 0.5rem 1rem;
                text-align: center;
                font-size: 0.85rem;
                flex-shrink: 0;
            }

            .skip-link {
                position: absolute;
                top: -100%;
                left: 50%;
                transform: translateX(-50%);
                z-index: 100;
                background: #000;
                color: #fff;
                padding: 0.5rem 1rem;
                border-radius: 0 0 4px 4px;
                font-size: 0.875rem;
                text-decoration: none;
                transition: top 0.15s ease;
            }

            .skip-link:focus {
                top: 0;
            }

            .page-wrapper {
                height: 100%;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                position: relative;
            }

            .page-wrapper-desktop {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                margin-right: auto;
                margin-left: auto;
                border: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }

            body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                flex:1;
            }

            header {
                background-color: #333;
                color: white;
                padding: 0.5rem;
                text-align: left;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            .collapsed-header-content {
                display: flex;
                align-items: center;
                gap: 0.75rem;
                justify-content: flex-start;
            }

            .collapsed-logo {
                width: 40px;
                height: 40px;
                object-fit: contain;
                flex-shrink: 0;
                border-radius: 4px;
                padding: 4px;
            }

            .collapsed-header-content h1 {
                margin: 0;
                font-size: 0.95rem;
                flex: 1;
            }

            .collapsed-header-buttons {
                display: flex;
                gap: 0.5rem;
                flex-wrap: wrap;
                justify-content: center;
            }

            header h1 {
                margin: 0.5rem 0px;
                font-size: 1rem;
            }

            nav {
                padding: 5px;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                border-bottom: 1px solid #ddd;
            }

            .full_challenge_page_link {
                padding: 5px;
                display: flex;
                justify-content: center;
                align-items: center;
                cursor: pointer;
            }

            nav[role="tablist"] {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                align-items: center;
                justify-content: center;
                margin: 0;
                padding: 0;
            }

            .nav-tab {
                display: inline;
                padding: 0.25rem 0.5rem;
                cursor: pointer;
                background: none;
                border: none;
                color: inherit;
                font: inherit;
                font-size: inherit;
            }

            .nav-tab:focus-visible {
                outline: 2px solid currentColor;
                outline-offset: 2px;
                border-radius: 2px;
            }

            .locations_section {
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                max-width:100%;
                height: 100%;
                overflow: hidden;
            }

            .business-list {
                padding-right: 0.5rem;
                padding-bottom: 2px;
                align-content: flex-start;
                justify-content: center;
                width: 100%;
            }

            .card {
                margin-bottom: 1rem;
                align-items: center;
            }

            .business-list::-webkit-scrollbar {
                width: 5px;
            }

            .business-list::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .business-list::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .active_tab {
                font-weight: bold;
            }

            main {
                padding: 0.75rem 5px 5px;
                flex:1;
                overflow-y: auto;
                overflow-x: hidden;
            }

            .content_main {
                max-width: 800px;
                width: 100%;
                margin-left: auto;
                margin-right: auto;
                flex: 1;
                display: flex;
                flex-direction: column;
                min-height: 0;
                overflow-y: hidden;
            }

            .content_main.content_main_mobile {
                width: calc(100% - 10px);
                max-width: 400px;
            }

            .inner_content_main {
                padding: 0;
            }

            .progress {
                text-align: center;
            }

            .header-buttons {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                max-width: 100%;
                gap: 1em;
                padding: 0.5rem;
            }

            .language-selector-corner {
                position: absolute;
                top: 0.5rem;
                right: 0.5rem;
                z-index: 2;
            }

            .cover-section {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0;
                overflow: hidden;
                transition: opacity 0.3s ease, visibility 0.3s ease, max-height 0.3s ease;
                flex-shrink: 0;
                max-height: 200px;
            }

            .cover-section.visible {
                opacity: 1;
                visibility: visible;
                max-height: 200px;
            }

            .cover-section.hidden {
                opacity: 0;
                visibility: hidden;
                max-height: 0;
                pointer-events: none;
            }

            section {
                height: 100%;
            }

            section#panel-activities {
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }

            section#panel-activities[hidden] {
                display: none;
            }

            section#panel-rewards,
            section#panel-leaderboard,
            section#panel-photo_feed {
                overflow-y: auto;
            }

            section#panel-rewards[hidden],
            section#panel-leaderboard[hidden],
            section#panel-photo_feed[hidden] {
                display: none;
            }

            .scrollable-list {
                scroll-behavior: smooth;
            }

            .loading-more {
                padding: 1rem;
                text-align: center;
                opacity: 0.8;
            }

            .end-of-list {
                padding: 1rem;
                text-align: center;
                color: var(--theme-lighter-text-color, #757575);
                font-size: 0.9rem;
            }

            .end-of-list p {
                margin: 0;
                font-style: italic;
            }

            .card,
            .business-card,
            .reward-card {
                padding: 0.5rem 1rem;
                display: flex;
                flex-direction: column;
                gap: 5px;
            }

            .card-with-shadow {
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }

            .card-with-border {
                border: 1px solid #ddd;
                border-radius: 5px;
            }

            .footer-stats {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 0.5rem;
                width: 80%
            }

            .stats-text {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                gap: 0.5rem;
                width: 100%;
            }

            .user_position {
                margin-top: 10px;
            }

            p {
                margin: 0;
                gap: 5px;
            }

            .business-list {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
            }

            .reward-card img {
                width: 24px;
            }

            .progress {
                text-align: center;
            }

            .business-card img {
                width: 25px;
                height: 25px;
            }

            .faq-button,
            .map-button,
            .support-button {
                background-color: #28a745;
                border: none;
                color: white;
                cursor: pointer;
                display: block;
                margin: 1em auto;
                padding: 0.5em 1em;
                text-align: center;
            }

            input[type="text"],
            input[type="email"],
            input[type="password"] {
                border: 1px solid #ddd;
                border-radius: 5px;
                padding: 0.5em;
                width: 100%;
                margin-bottom: 1em;
            }

            footer {
                padding: 0.5em;
                padding-bottom: 1.5em;
                text-align: center;
                display: flex;
                justify-content: center;
                display: flex;
                flex-direction: column;
                align-items: center;
                border-top: 1px solid var(--theme-border-color);
                border-radius: 8px 8px 0 0;
                position: relative;
            }

            footer p {
                margin: 0;
            }

            setting-drawer {
                z-index:2;
                --bottom-height: 0px;
            }

            .image_area {
                position: relative;
                margin-top: -0.75rem;
                min-width: 100%;
                max-height: 15vh;
                overflow: hidden;
                display: flex;
                justify-content: center;
                align-items: center;
            }

            .cover {
                display: flex;
                object-fit: cover;
                min-width: 100%;
                min-height: 100%;
                max-width: 100%;
                max-height: 100%;
            }

            .logo {
                position: absolute;
                max-width: 70%; /* Smaller logo */
                max-height: 70%; /* Smaller logo */
                object-fit: contain;
                background-color: white;
                padding: 5px; /* Padding for white background */
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); /* Optional: add shadow for better visibility */
                z-index: 2; /* Ensure logo is on top */
            }

            h3 {
                margin-top: 0.5rem;
                margin-bottom: 0.5rem;
            }


            .text_area {
                padding: 0 1em;
                text-align: center;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            .sort_and_filter {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                margin-bottom: 0.5rem;
                margin-top: 0.5rem;
                gap: 1em;
                width: 100%;
            }

            .stats-card__container {
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                flex-wrap: wrap;
                gap: 6px;
            }

            .stats-card {
                flex: 1;
                max-width: 180px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 40px;
                padding: 0 16px;
                border-radius: 12px;
                border: 1px solid #CAC4D0;
                gap: 4px;
                box-sizing: border-box;
            }

            .stats-card__value {
                font-size: 24px;
                font-weight: 700;
                line-height: 130%;
                color: #000000;
            }

            .stats-card__label {
                color: #1D1B20;
                text-align: center;
                line-height: 125%;
            }

            .reward-details {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 1em;
            }

            .reward-details img {
                height: 50px;
            }

            h2 {
                margin-bottom:0.5em;
                font-size: 1rem;
            }

            .challenge-leaders__content {
                display: flex;
                align-items: center;
            }

            .challenge-leaders__user-name {
                display: flex;
                align-items: center;
                flex-direction: column;
                justify-content: center;
                align-items: flex-start;
            }

            .award-icon {
                width: 20px;
                height: 20px;
                margin-right: -2px;
                border: 1px solid var(--theme-border-color);
                border-radius: 50%;
            }

            .challenge-leaders__places {
                margin-left: 10px;
                color: #777;
            }

            .challenge-leaders__point {
                font-weight: bold;
                color: #444;
            }

            #rewards {
                overflow-y: auto;
            }

            .earned-rewards {
                display: flex;
                flex-wrap: wrap;
                gap: 1em;
                flex-direction: row;
            }

            .reward-card {
                display: flex;
                flex-direction: column;
                align-items: center;
                width: calc(50% - 1rem);
                padding: 0.5rem 0;
                text-align: center;

            }

            .floating-checkin-btn {
                position: absolute;
                top: -56px;
                right: 30px;
                width: 52px;
                height: 52px;
                z-index: 1;
                padding: 0 !important;
                display: flex;
                align-items: center;
                justify-content: center;
                animation: slideUp 0.4s ease-out;
            }

            @keyframes slideUp {
                from {
                    opacity: 0;
                    transform: translateY(20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

        `;
    }
}
